import { formatNumberCommas } from "./formatters";
import { ACTIVE_CURRENCIES } from "./currency";

export const RATE_INPUT_PATTERN = /^\d*(?:\.\d{0,2})?$/;
const ACTIVE_CURRENCY_SET = new Set(ACTIVE_CURRENCIES);

export const getCurrencyFractionDigits = (currency?: string): number => {
	const code = (currency || "").trim().toUpperCase();
	if (!code || !ACTIVE_CURRENCY_SET.has(code)) {
		return 2;
	}

	try {
		return (
			new Intl.NumberFormat("en-US", {
				style: "currency",
				currency: code,
			}).resolvedOptions().maximumFractionDigits ?? 2
		);
	} catch {
		return 2;
	}
};

export const getRateInputPattern = (currency?: string): RegExp =>
	getCurrencyFractionDigits(currency) === 0 ? /^\d*$/ : RATE_INPUT_PATTERN;

export const sanitizeRateInputValue = (raw: string, currency?: string): string => {
	const normalized = raw.replace(/[^\d.]/g, "");
	if (getCurrencyFractionDigits(currency) === 0) {
		return normalized.replace(/\./g, "");
	}

	const dotIndex = normalized.indexOf(".");
	if (dotIndex === -1) {
		return normalized;
	}

	const integerPart = normalized.slice(0, dotIndex);
	const decimalPart = normalized.slice(dotIndex + 1).replace(/\./g, "");
	return `${integerPart}.${decimalPart.slice(0, 2)}`;
};

export const formatRateInputValue = (value: unknown): string => {
	if (value === null || value === undefined || value === "") {
		return "";
	}

	const n = Number(value);
	if (!Number.isFinite(n) || n < 0) {
		return "";
	}

	return n.toString();
};

export const formatRateDisplayValue = (value: unknown, currency?: string): string => {
	if (value === null || value === undefined || value === "") {
		return "";
	}

	const sanitized = String(value).replace(/[^\d.]/g, "");
	if (!sanitized) {
		return "";
	}

	const firstDecimalIndex = sanitized.indexOf(".");
	const normalizedValue =
		firstDecimalIndex === -1
			? sanitized
			: `${sanitized.slice(0, firstDecimalIndex + 1)}${sanitized.slice(firstDecimalIndex + 1).replace(/\./g, "")}`;

	const [rawIntegerPart, rawFractionalPart] = normalizedValue.split(".", 2);
	const integerPart = (rawIntegerPart || "").replace(/^0+(?=\d)/, "") || "0";
	const formattedIntegerPart = formatNumberCommas(integerPart);
	if (getCurrencyFractionDigits(currency) === 0) {
		return formattedIntegerPart;
	}

	if (rawFractionalPart === undefined) {
		return formattedIntegerPart;
	}

	return `${formattedIntegerPart}.${rawFractionalPart.slice(0, 2)}`;
};

export const formatRateMoneyDisplayValue = (value: unknown, currency?: string): string => {
	if (value === null || value === undefined || value === "") {
		return "";
	}

	const sanitized = String(value).replace(/,/g, "").trim();
	if (sanitized === "") {
		return "";
	}

	const numericValue = Number(sanitized);
	if (!Number.isFinite(numericValue) || numericValue < 0) {
		return "";
	}

	const fractionDigits = getCurrencyFractionDigits(currency);
	return new Intl.NumberFormat("en-US", {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	}).format(numericValue);
};

export const isRateInputValid = (raw: string, currency?: string): boolean =>
	getRateInputPattern(currency).test(raw.replace(/,/g, "").trim());

export const parseRateInputValue = (raw: string, currency?: string): number | null | "invalid" => {
	const trimmed = raw.replace(/,/g, "").trim();
	if (trimmed === "") {
		return null;
	}
	if (!isRateInputValid(trimmed, currency)) {
		return "invalid";
	}

	const parsed = Number(trimmed);
	if (!Number.isFinite(parsed) || parsed < 0) {
		return "invalid";
	}

	return parsed;
};
